/**
 * Single-version content renderer for the doc-engine.
 *
 * Resolves special HTML nodes (refs, headings, captions, blocks, tables, lists)
 * into displayable HTML. Used by:
 * - The diff engine for block expansion (entirely inserted/deleted blocks)
 * - FE changes mode for single-version rendering
 * - Future BE PDF export for resolving special nodes
 *
 * IMPORTANT: This file must have ZERO product imports - only engine imports.
 */
import * as cheerio from 'cheerio';
import type { CheerioAPI } from 'cheerio';
import type { Element } from 'domhandler';

import { formatReferenceHtml } from '../runtime/formatReference';
import { escapeHtml } from '../renderer/html';
import { injectColgroups } from '../renderer/injectColgroups';
import { ID_ATTR, HEADING_TAGS, isNodeLoaded } from '../schema';
import type { TDocDiffRuntime, TDocDiffOptions } from '../runtime/types';
import { stripZeroWidthSpaces } from '../text';
import {
  REFERENCE_LOAD_ERROR,
  INLINE_BLOCK_LOAD_ERROR,
  INLINE_BLOCK_NOT_LOADED,
  BLOCK_LOAD_ERROR,
  BLOCK_NOT_LOADED,
  wrapStaticBlock,
} from './constants';
import { formatCitationLabel, parseIndexAttr } from '../text';
import { normalizeInlineBlockValue } from './helpers';
import { TEMPLATE_FIELD_KINDS, templateFieldWrapperClass } from '../templateFields';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TContentHooks = {
  /** Post-process images (e.g. wrapImagesForAlign). Called after node resolution. */
  processImages?: ($: CheerioAPI, $root: cheerio.Cheerio<Element>) => void;
  /** Post-process final HTML string (e.g. LaTeX pre-rendering). */
  processLatex?: (html: string) => string;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const COMPLEX_NODES =
  'table, caption-node, ref, literature-citation, reusable-block, inline-block, div.page-break, h1, h2, h3, h4, h5, h6, ol, ul';

const MAX_CONTENT_DEPTH = 5;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getStringAttrs = (el: Element): Record<string, string> => {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(el.attribs || {})) {
    if (typeof value === 'string') result[key] = value;
  }
  return result;
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Renders HTML with all special nodes resolved for a single version.
 *
 * The function walks complex nodes (refs, headings, captions, blocks, etc.)
 * and resolves them via the runtime's data methods + engine formatting.
 * Platform-specific processing (images, LaTeX) is handled via hooks.
 */
export const renderContent = async (
  html: string,
  runtime: TDocDiffRuntime,
  opts: TDocDiffOptions,
  side: 'current' | 'previous',
  hooks?: TContentHooks,
  rootSelector?: string,
  depth: number = 0
): Promise<string> => {
  const $ = cheerio.load(html);

  // Normalize <title-node> → <p class="title-node">
  if (!rootSelector) {
    const $titleNode = $('title-node');
    if ($titleNode.length > 0) {
      const $newTitleNode = $('<p></p>')
        .addClass('title-node')
        .html($titleNode.html() as string);
      $titleNode.replaceWith($newTitleNode);
    }
  }

  const $root = rootSelector
    ? ($(rootSelector).first() as cheerio.Cheerio<Element>)
    : ($('body') as cheerio.Cheerio<Element>);

  // Never style the bare tag instead: the PDF export renders it, and it builds no wrapper on purpose.
  for (const { name } of TEMPLATE_FIELD_KINDS) $root.find(name).addClass(templateFieldWrapperClass(name));

  // Inject colgroups for table column widths
  if (!rootSelector) {
    const tableHtml = $root.find('table').length ? injectColgroups($.html()) : null;
    if (tableHtml) {
      const $2 = cheerio.load(tableHtml);
      $root.find('table').each((i, el) => {
        const $newTable = $2('table').eq(i);
        if ($newTable.length) $(el).replaceWith($newTable);
      });
    }
  }

  // Walk complex nodes (top-level only - skip nested ones)
  const promises = $root
    .find(COMPLEX_NODES)
    .toArray()
    .map(async (elem) => {
      const $elem = $(elem);
      const hasComplexAncestorNotRoot = $elem.parents(COMPLEX_NODES).not($root).length > 0;
      if (hasComplexAncestorNotRoot) return;

      const tag = elem.tagName;
      const id = $elem.attr(ID_ATTR) || '';

      let result: string | undefined;

      if (tag === 'ref') {
        const attrs = getStringAttrs(elem);
        const data = runtime.resolveReference(attrs, side);
        if (!data) {
          result = `<span ${ID_ATTR}="${escapeHtml(id)}" class="ref-error">${escapeHtml(REFERENCE_LOAD_ERROR)}</span>`;
        } else {
          result = formatReferenceHtml(data, attrs, opts);
        }
      } else if (tag === 'literature-citation') {
        // Stored empty - the editor's node view draws the chip - so it must be drawn here or the
        // citation renders as nothing in PDF export and the static half.
        if (!$elem.text()) {
          $elem.addClass('literature-citation').text(formatCitationLabel(parseIndexAttr($elem.attr('data-index'))));
        }
      } else if (tag === 'caption-node') {
        const bookmarkKind = $elem.attr('data-bookmark-kind') || '';
        const index = $elem.attr('data-index') || '';
        const align = $elem.attr('data-align') || 'left';
        const prefix = runtime.resolveCaptionPrefix(bookmarkKind, index, side);
        // Html, not text: a caption's schema allows marks, and text() drops them.
        const content = stripZeroWidthSpaces($elem.find('span.caption-content').html() ?? '');
        result = `<span id="${escapeHtml(id)}" class="caption-wrapper" data-align="${escapeHtml(align)}" data-bookmark-kind="${escapeHtml(bookmarkKind)}" data-index="${escapeHtml(index)}" ${ID_ATTR}="${escapeHtml(id)}"><span class="caption-prefix">${escapeHtml(prefix)}</span> <span class="caption-content">${content}</span></span>`;
      } else if (HEADING_TAGS.has(tag)) {
        const headingPair = runtime.resolveHeading(id);
        const heading = side === 'current' ? headingPair.current : headingPair.previous;
        const headingText = $elem.html() || '';

        if (heading) {
          if (heading.level === 5 && opts?.isH5RefEnabled) {
            result = `<${tag} id="${escapeHtml(id)}"><span class="heading-text h5-ref">${headingText}</span></${tag}>`;
          } else {
            result = `<${tag} id="${escapeHtml(id)}"><span class="heading-label">${escapeHtml(heading.numbering)}</span><span class="heading-text">${headingText}</span></${tag}>`;
          }
        } else {
          result = `<${tag} id="${escapeHtml(id)}"><span class="heading-text">${headingText}</span></${tag}>`;
        }
      } else if (tag === 'table') {
        const tableHtml = await renderContent($elem.toString(), runtime, opts, side, hooks, 'table', depth);
        result = `<div class="tableWrapper">${tableHtml}</div>`;
      } else if (tag === 'ol' || tag === 'ul') {
        result = await renderContent($elem.toString(), runtime, opts, side, hooks, tag, depth);
      } else if (tag === 'reusable-block' || tag === 'inline-block') {
        const attrs = getStringAttrs(elem);
        // A block whose registration has not settled cannot be resolved, and asking the runtime for it
        // reports a load failure for something that is not broken. The editor shows a placeholder.
        const loaded = runtime.isNodeLoaded?.(attrs, side) ?? isNodeLoaded(attrs);
        const blockInfo = loaded ? await runtime.getBlockInfo(id, attrs, side) : undefined;

        if (tag === 'inline-block') {
          if (!loaded) {
            result = `<span class="inline-block-styled">${escapeHtml(INLINE_BLOCK_NOT_LOADED)}</span>`;
          } else {
            const value = normalizeInlineBlockValue(blockInfo?.html || INLINE_BLOCK_LOAD_ERROR);
            const inlineClass = blockInfo ? '' : 'inline-block-error';
            const className = ['inline-block-styled', inlineClass].filter(Boolean).join(' ');
            result = `<span class="${className}">${escapeHtml(value)}</span>`;
          }
        } else {
          let content: string;
          if (!loaded) {
            content = `<span>${escapeHtml(BLOCK_NOT_LOADED)}</span>`;
          } else if (blockInfo) {
            if (depth >= MAX_CONTENT_DEPTH) {
              content = blockInfo.html;
            } else {
              content = await renderContent(blockInfo.html, runtime, opts, side, hooks, undefined, depth + 1);
            }
          } else {
            content = `<span class="block-error">${escapeHtml(BLOCK_LOAD_ERROR)}</span>`;
          }
          result = wrapStaticBlock(runtime.renderBlockHeader?.(attrs, side) ?? '', content);
        }
      } else if (tag === 'div' && $elem.hasClass('page-break')) {
        result = '<div class="page-break"></div>';
      }

      if (result != null) $elem.replaceWith(result);
    });

  await Promise.all(promises);

  // Platform-specific post-processing via hooks
  hooks?.processImages?.($, $root);

  // When rootSelector is provided, return outerHTML to preserve the root element.
  // This fixes the table-in-block bug where $root.html() strips the <table> tag.
  const rawHtml = rootSelector ? $.html($root) : $root.html() || '';

  return hooks?.processLatex?.(rawHtml) ?? rawHtml;
};
